(() => {
  if (window.__acsProductSpinViewerBootstrapped) {
    if (typeof window.__acsInitializeProductSpinViewers === 'function') {
      window.__acsInitializeProductSpinViewers(document);
    }
    return;
  }
  window.__acsProductSpinViewerBootstrapped = true;

  const idle = window.requestIdleCallback || ((callback) => window.setTimeout(() => callback({ timeRemaining: () => 0 }), 200));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobileSpinMediaQuery = window.matchMedia('(max-width: 767px)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const slowConnection = Boolean(navigator.connection && /(^|-)2g$/.test(navigator.connection.effectiveType || ''));

  const normalizeConfig = (input = {}) => {
    const base = input.base || '';
    const count = Number(input.count || 0);
    const pad = Number(input.pad || 3);
    const ext = input.ext || 'webp';
    const version = input.version || '';
    const poster = input.poster || '';
    const title = input.title || '';
    const options = Array.isArray(input.options) ? input.options : [];
    const values = Array.isArray(input.values) ? input.values : [];
    if (!base || !Number.isFinite(count) || count <= 0) return null;
    return { base, count, pad, ext, version, poster, title, options, values };
  };

  const configFromDataset = (viewer) => normalizeConfig({
    base: viewer.dataset.frameBase || '',
    count: viewer.dataset.frameCount || 0,
    pad: viewer.dataset.framePad || 3,
    ext: viewer.dataset.frameExt || 'webp',
    version: viewer.dataset.frameVersion || '',
    poster: viewer.dataset.posterUrl || '',
  });

  const productFallbackConfig = (viewer) => normalizeConfig({
    base: viewer.dataset.productFrameBase || viewer.dataset.frameBase || '',
    count: viewer.dataset.productFrameCount || viewer.dataset.frameCount || 0,
    pad: viewer.dataset.framePad || 3,
    ext: viewer.dataset.frameExt || 'webp',
    version: viewer.dataset.productFrameVersion || viewer.dataset.frameVersion || '',
    poster: viewer.dataset.posterUrl || '',
  });

  const buildFrames = (config) => {
    if (!config) return [];
    return Array.from({ length: config.count }, (_, index) => {
      const frame = String(index + 1).padStart(config.pad, '0');
      return new URL(`${config.base}${frame}.${config.ext}`, window.location.href).href;
    });
  };

  const parseVariantConfigs = (viewer) => {
    const node = viewer.querySelector('[data-product-spin-variant-frames]');
    if (!node || !node.textContent.trim()) return new Map();

    try {
      const raw = JSON.parse(node.textContent);
      return new Map(Object.entries(raw).map(([variantId, config]) => [
        String(variantId),
        normalizeConfig({
          base: config.base,
          count: config.count,
          version: config.version,
          poster: config.poster,
          title: config.title,
          options: config.options,
          values: config.values,
          pad: viewer.dataset.framePad || 3,
          ext: viewer.dataset.frameExt || 'webp',
        }),
      ]));
    } catch (_error) {
      return new Map();
    }
  };

  const selectedVariantIdFromField = (field) => {
    if (!field) return '';
    if (field.matches('select')) return field.value;
    if ((field.matches('[type="radio"]') || field.matches('[type="checkbox"]')) && !field.checked) return '';
    return field.value || '';
  };

  const normalizeOptionValue = (value) => String(value || '').trim().toLowerCase();

  const optionNameFromControl = (field, productId) => {
    const rawName = field.getAttribute('name') || '';
    const optionsMatch = rawName.match(/^options\[(.+)\]$/);
    if (optionsMatch) return optionsMatch[1].trim();
    if (productId && rawName.startsWith(`${productId}-`)) return rawName.slice(String(productId).length + 1).trim();

    const fieldset = field.closest('fieldset');
    const legend = fieldset && fieldset.querySelector('legend');
    return legend ? legend.textContent.trim() : '';
  };

  const selectedOptionsFromControls = (viewer) => {
    const productId = viewer.dataset.productId || '';
    const controls = document.querySelectorAll('.js-variants input[type="radio"]:checked, .js-variants select');
    const byName = new Map();
    const values = [];

    controls.forEach((field) => {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) return;
      if (field.disabled) return;

      const rawName = field.getAttribute('name') || '';
      if (productId && rawName && !rawName.startsWith(`${productId}-`) && !rawName.startsWith('options[')) return;

      const value = field.value || '';
      if (!value) return;

      const optionName = optionNameFromControl(field, productId);
      if (optionName) byName.set(optionName, value);
      values.push(value);
    });

    return { byName, values };
  };

  const initializeProductSpinViewers = (root = document) => {
    const viewers = [];
    if (root instanceof Element && root.matches('[data-product-spin-viewer]')) viewers.push(root);
    if (root && typeof root.querySelectorAll === 'function') {
      viewers.push(...root.querySelectorAll('[data-product-spin-viewer]'));
    }

    viewers.forEach((viewer) => {
    if (viewer.dataset.productSpinReady === 'true') return;
    viewer.dataset.productSpinReady = 'true';

    const stage = viewer.querySelector('[data-product-spin-stage]');
    const image = viewer.querySelector('[data-product-spin-image]');
    const range = viewer.querySelector('[data-product-spin-range]');
    const statusNode = viewer.querySelector('[data-product-spin-status]');
    const hintNode = viewer.querySelector('[data-product-spin-hint]');
    const activateNode = viewer.querySelector('[data-product-spin-activate]');
    const variantConfigs = parseVariantConfigs(viewer);
    const fallbackConfig = productFallbackConfig(viewer);
    const frameStatusTemplate = viewer.dataset.frameStatusTemplate || 'Frame __current__ of __total__';
    let activeConfig = configFromDataset(viewer) || fallbackConfig;
    let frames = buildFrames(activeConfig);
    const posterMode = viewer.dataset.posterMode === 'true';

    if (!stage || !image || !range || !frames.length) return;

    const imageAlt = image.getAttribute('alt') || '';
    const bufferImage = image.cloneNode(false);
    bufferImage.classList.remove('is-active');
    bufferImage.removeAttribute('data-product-spin-image');
    bufferImage.setAttribute('aria-hidden', 'true');
    bufferImage.setAttribute('alt', '');
    stage.appendChild(bufferImage);

    const clamp = (index) => Math.min(Math.max(index, 0), frames.length - 1);

    const initialFrameIndex = () => {
      const initial = Number(viewer.dataset.initialFrame || 1);
      const normalized = Number.isFinite(initial) && initial > 0 ? initial : 1;
      return clamp(normalized - 1);
    };

    let frame = initialFrameIndex();
    let decodedFrames = new Map([[frame, frames[frame]]]);
    let pendingFrames = new Map();
    let activeImage = image;
    let standbyImage = bufferImage;
    let idleStarted = false;
    let statusTimer = 0;
    let wheelDelta = 0;
    let renderToken = 0;
    let lastVariantId = viewer.dataset.currentVariantId || '';
    let posterActive = Boolean(posterMode && activeConfig && activeConfig.poster);
    let controlsReady = !posterActive;

    const waitForImage = (targetImage) => {
      if (targetImage.complete && targetImage.naturalWidth > 0) return Promise.resolve(true);

      const loadPromise = new Promise((resolve) => {
        targetImage.onload = () => resolve(targetImage.naturalWidth > 0);
        targetImage.onerror = () => resolve(false);
      });

      if (!targetImage.decode) return loadPromise;
      return targetImage.decode()
        .then(() => targetImage.naturalWidth > 0)
        .catch(() => loadPromise);
    };

    const updateRangeProgress = () => {
      const max = Math.max(frames.length - 1, 1);
      range.style.setProperty('--product-spin-progress', `${(frame / max) * 100}%`);
    };

    const updateStatus = () => {
      const visibleFrame = frame + 1;
      const frameStatus = frameStatusTemplate
        .replace(/__current__/g, String(visibleFrame))
        .replace(/__total__/g, String(frames.length));
      range.min = '1';
      range.max = String(frames.length);
      range.step = '1';
      range.value = String(visibleFrame);
      range.setAttribute('aria-valuetext', frameStatus);
      updateRangeProgress();

      if (!statusNode) return;
      window.clearTimeout(statusTimer);
      statusTimer = window.setTimeout(() => {
        statusNode.textContent = frameStatus;
      }, 120);
    };

    const stopSpinTouchLeak = (node, options = {}) => {
      if (!node) return;
      const preventMove = Boolean(options.preventMove);
      const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'touchstart', 'touchmove', 'touchend'];

      events.forEach((eventName) => {
        node.addEventListener(eventName, (event) => {
          event.stopPropagation();
          if (preventMove && event.cancelable && (eventName === 'touchmove' || eventName === 'pointermove')) {
            event.preventDefault();
          }
        }, { capture: true, passive: false });
      });
    };

    const setControlsReady = (ready) => {
      controlsReady = Boolean(ready);
      viewer.classList.toggle('is-spin-ready', controlsReady);

      if (activateNode) {
        activateNode.setAttribute('aria-hidden', controlsReady ? 'true' : 'false');
        activateNode.tabIndex = controlsReady ? -1 : 0;
      }

      range.disabled = !controlsReady;
      range.tabIndex = controlsReady ? 0 : -1;

      if (!hintNode) return;
      if (controlsReady && !viewer.classList.contains('is-spin-active')) {
        hintNode.removeAttribute('aria-hidden');
      } else {
        hintNode.setAttribute('aria-hidden', 'true');
      }
    };

    const syncPosterClass = () => {
      viewer.classList.toggle('is-poster-active', posterActive);
    };

    const focusSpinSlide = (speed = 0) => {
      const slider = viewer.closest('.js-product-media-items');
      if (!slider || !slider.swiper) return;
      if (typeof slider.swiper.slideToLoop === 'function') {
        slider.swiper.slideToLoop(0, speed);
        return;
      }
      slider.swiper.slideTo(0, speed);
    };

    const scheduleSpinSlideFocus = () => {
      if (!posterMode) return;
      [0, 80, 240, 500].forEach((delay) => {
        window.setTimeout(() => focusSpinSlide(0), delay);
      });
    };

    const showCurrentFramePreview = () => {
      if (!posterActive || !frames[frame]) return;
      posterActive = false;
      syncPosterClass();
      activeImage.src = frames[frame];
      decodedFrames.set(frame, frames[frame]);
      preloadAround(frame, saveData || slowConnection ? 1 : 4);
    };

    const activateControls = (event) => {
      if (!posterMode || controlsReady) return;
      if (event) event.preventDefault();
      showCurrentFramePreview();
      setControlsReady(true);
      focusSpinSlide(0);
    };

    const markSpinActive = () => {
      if (!controlsReady) setControlsReady(true);
      if (!posterActive) return;
      posterActive = false;
      syncPosterClass();
      viewer.classList.add('is-spin-active');
      if (hintNode) hintNode.setAttribute('aria-hidden', 'true');
    };

    const updateLinkedThumb = (poster) => {
      if (!poster) return;
      const mediaSlider = viewer.closest('.m-product-media');
      const optionThumbs = mediaSlider && mediaSlider.querySelectorAll('[data-product-spin-option-thumb]');
      if (optionThumbs && optionThumbs.length) {
        syncOptionThumbs(activeConfig);
        return;
      }

      const thumbImage = mediaSlider && mediaSlider.querySelector('[data-product-spin-thumb]');
      if (!thumbImage) return;
      thumbImage.setAttribute('src', poster);
      thumbImage.removeAttribute('srcset');
    };

    const syncOptionThumbs = (config = activeConfig) => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider) return;

      const optionThumbs = mediaSlider.querySelectorAll('[data-product-spin-option-thumb]');
      if (!optionThumbs.length) return;

      const values = config && Array.isArray(config.values) ? config.values.map(normalizeOptionValue) : [];
      const title = normalizeOptionValue(config && config.title);

      optionThumbs.forEach((thumb) => {
        const value = normalizeOptionValue(thumb.dataset.productSpinOptionValue);
        const active = values.includes(value) || title === value;
        thumb.classList.toggle('is-spin-option-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    const resetPosterState = () => {
      if (!posterMode || !activeConfig || !activeConfig.poster) return;

      posterActive = true;
      controlsReady = false;
      viewer.classList.remove('is-spin-active');
      activeImage.src = activeConfig.poster;
      activeImage.classList.add('is-active');
      activeImage.setAttribute('data-product-spin-image', '');
      activeImage.removeAttribute('aria-hidden');
      activeImage.setAttribute('alt', imageAlt);
      standbyImage.removeAttribute('src');
      standbyImage.classList.remove('is-active');
      standbyImage.removeAttribute('data-product-spin-image');
      standbyImage.setAttribute('aria-hidden', 'true');
      standbyImage.setAttribute('alt', '');
      syncPosterClass();
      setControlsReady(false);
      updateStatus();
      preloadAround(frame, saveData || slowConnection ? 1 : 4);
    };

    const selectVariantControl = (value) => {
      const selectedValue = normalizeOptionValue(value);
      if (!selectedValue) return false;

      const productId = viewer.dataset.productId || '';
      let selected = false;

      document.querySelectorAll('.js-variants input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement) || input.disabled) return;

        const rawName = input.getAttribute('name') || '';
        if (productId && rawName && !rawName.startsWith(`${productId}-`) && !rawName.startsWith('options[')) return;
        if (normalizeOptionValue(input.value) !== selectedValue) return;

        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        selected = true;
      });

      if (selected) return true;

      document.querySelectorAll('.js-variants select').forEach((select) => {
        if (!(select instanceof HTMLSelectElement) || select.disabled) return;

        const rawName = select.getAttribute('name') || '';
        if (productId && rawName && !rawName.startsWith(`${productId}-`) && !rawName.startsWith('options[')) return;

        const option = Array.from(select.options).find((item) => normalizeOptionValue(item.value) === selectedValue);
        if (!option) return;

        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        selected = true;
      });

      return selected;
    };

    const slideMainToMediaId = (mediaId, speed = 0) => {
      const mediaSlider = viewer.closest('.m-product-media');
      const slider = mediaSlider && mediaSlider.querySelector('.js-product-media-items');
      if (!slider || !mediaId) return;

      const slideToMedia = () => {
        const slide = Array.from(slider.querySelectorAll('.swiper-slide[data-media-id]')).find(
          (item) => item.dataset.mediaId === String(mediaId),
        );
        if (!slide) return;

        if (slider.swiper) {
          const index = Array.from(slider.swiper.slides).indexOf(slide);
          if (index >= 0) slider.swiper.slideTo(index, speed);
          return;
        }

        slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      slideToMedia();
      [80, 240, 500].forEach((delay) => {
        window.setTimeout(slideToMedia, delay);
      });
    };

    const setActiveMediaThumb = (thumb) => {
      const thumbs = thumb && thumb.closest('.js-product-media-thumbs');
      if (!thumbs) return;

      thumbs.querySelectorAll('.m-product-media__thumb').forEach((item) => {
        item.classList.remove('swiper-slide-thumb-active');
      });
      thumb.classList.add('swiper-slide-thumb-active');

      if (thumbs.swiper) {
        const index = Array.from(thumbs.swiper.slides).indexOf(thumb);
        if (index >= 0) thumbs.swiper.slideTo(index, 0);
      }
    };

    const setActiveMediaMode = (button) => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider) return;

      mediaSlider.querySelectorAll('[data-product-media-mode-button]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const mediaModeButtonForId = (mediaId) => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider || !mediaId) return null;

      return Array.from(mediaSlider.querySelectorAll('[data-product-media-mode-button]')).find(
        (button) => button.dataset.mediaId === String(mediaId),
      ) || null;
    };

    const mediaThumbForId = (mediaId) => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider || !mediaId) return null;

      return Array.from(mediaSlider.querySelectorAll('.m-product-media__thumb[data-media-id]')).find(
        (thumb) => thumb.dataset.mediaId === String(mediaId),
      ) || null;
    };

    const pauseProductMediaVideos = () => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider) return;
      mediaSlider.querySelectorAll('video').forEach((video) => video.pause());
    };

    const activateSpinMediaMode = (resetPoster = true) => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider) return;

      const spinButton = mediaSlider.querySelector('[data-product-media-mode-button="spin"]');
      const spinSlide = mediaSlider.querySelector('.m-product-media__spin[data-media-id]');
      const mediaId = (spinButton && spinButton.dataset.mediaId) || (spinSlide && spinSlide.dataset.mediaId);
      if (!mediaId) return;

      pauseProductMediaVideos();
      if (resetPoster) resetPosterState();
      slideMainToMediaId(mediaId, 0);
      if (spinButton) setActiveMediaMode(spinButton);

      const optionThumb = mediaSlider.querySelector('[data-product-spin-option-thumb].is-spin-option-active');
      const spinThumb = optionThumb || mediaThumbForId(mediaId);
      if (spinThumb) setActiveMediaThumb(spinThumb);
    };

    const activateMediaModeButton = (button) => {
      if (!button) return;
      const mode = button.dataset.productMediaModeButton || '';
      if (mode === 'spin') {
        activateSpinMediaMode(true);
        return;
      }

      const mediaId = button.dataset.mediaId || '';
      if (!mediaId) return;
      pauseProductMediaVideos();
      slideMainToMediaId(mediaId, 0);
      setActiveMediaMode(button);

      const thumb = mediaThumbForId(mediaId);
      if (thumb) setActiveMediaThumb(thumb);
    };

    const configureMobileStaticMedia = () => {
      const mediaSlider = viewer.closest('.m-product-media--has-spin-first');
      if (!mediaSlider || mediaSlider.dataset.productSpinMobileStaticBound === 'true') return;

      mediaSlider.dataset.productSpinMobileStaticBound = 'true';
      const mainSlider = mediaSlider.querySelector('.js-product-media-items');
      const thumbsSlider = mediaSlider.querySelector('.js-product-media-thumbs');
      let mobileModeInitialized = false;

      const apply = () => {
        const lockTouch = mobileSpinMediaQuery.matches;

        [mainSlider, thumbsSlider].forEach((slider) => {
          if (!slider || !slider.swiper) return;
          slider.swiper.allowTouchMove = !lockTouch;
          slider.swiper.params.allowTouchMove = !lockTouch;
          slider.swiper.params.simulateTouch = !lockTouch;
          slider.swiper.update();
        });

        if (lockTouch && mainSlider && mainSlider.swiper && !mobileModeInitialized) {
          mobileModeInitialized = true;
          activateSpinMediaMode(false);
        } else if (!lockTouch) {
          mobileModeInitialized = false;
        }

        if (mainSlider && mainSlider.swiper && mainSlider.dataset.productMediaModeSyncBound !== 'true') {
          mainSlider.dataset.productMediaModeSyncBound = 'true';
          mainSlider.swiper.on('slideChange', () => {
            const activeSlide = mainSlider.swiper.slides[mainSlider.swiper.activeIndex];
            const modeButton = activeSlide && mediaModeButtonForId(activeSlide.dataset.mediaId);
            if (modeButton) setActiveMediaMode(modeButton);
          });
        }
      };

      const scheduleApply = () => {
        apply();
        [80, 240, 600].forEach((delay) => {
          window.setTimeout(apply, delay);
        });
      };

      scheduleApply();

      if (typeof mobileSpinMediaQuery.addEventListener === 'function') {
        mobileSpinMediaQuery.addEventListener('change', scheduleApply);
      } else if (typeof mobileSpinMediaQuery.addListener === 'function') {
        mobileSpinMediaQuery.addListener(scheduleApply);
      }
    };

    const bindMediaThumbs = () => {
      const mediaSlider = viewer.closest('.m-product-media');
      if (!mediaSlider || mediaSlider.dataset.productSpinThumbsBound === 'true') return;

      mediaSlider.dataset.productSpinThumbsBound = 'true';
      configureMobileStaticMedia();
      mediaSlider.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const modeButton = event.target.closest('[data-product-media-mode-button]');
        if (modeButton && mediaSlider.contains(modeButton)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          activateMediaModeButton(modeButton);
          return;
        }

        const thumb = event.target.closest('.m-product-media__thumb[data-media-id]');
        if (!thumb || !mediaSlider.contains(thumb)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const isOptionThumb = thumb.hasAttribute('data-product-spin-option-thumb');
        if (isOptionThumb) {
          selectVariantControl(thumb.dataset.productSpinOptionValue);

          const variantId = String(thumb.dataset.productSpinVariantId || '');
          if (variantId && variantConfigs.has(variantId)) {
            lastVariantId = '';
            updateForVariantId(variantId);
          } else {
            updateForCurrentVariant();
          }

          resetPosterState();
          syncOptionThumbs(activeConfig);
        }

        slideMainToMediaId(thumb.dataset.mediaId, 0);
        setActiveMediaThumb(thumb);
        const linkedModeButton = mediaModeButtonForId(thumb.dataset.mediaId);
        if (linkedModeButton) setActiveMediaMode(linkedModeButton);
      }, true);
    };

    const loadFrame = (index) => {
      const next = clamp(index);
      if (decodedFrames.has(next)) return Promise.resolve(decodedFrames.get(next));
      if (pendingFrames.has(next)) return pendingFrames.get(next);

      const preloader = new Image();
      preloader.decoding = 'async';
      preloader.src = frames[next];

      const pending = waitForImage(preloader)
        .then((loaded) => {
          if (!loaded) return null;
          decodedFrames.set(next, frames[next]);
          return frames[next];
        })
        .finally(() => {
          pendingFrames.delete(next);
        });

      pendingFrames.set(next, pending);
      return pending;
    };

    const preloadAround = (center, radius = 3) => {
      for (let offset = 1; offset <= radius; offset += 1) {
        loadFrame(center + offset);
        loadFrame(center - offset);
      }
    };

    const beginIdlePreload = () => {
      if (idleStarted || saveData || slowConnection || reduceMotion) return;
      idleStarted = true;
      let cursor = 0;
      const step = () => {
        let count = 0;
        while (cursor < frames.length && count < 4) {
          loadFrame(cursor);
          cursor += 1;
          count += 1;
        }
        if (cursor < frames.length) idle(step);
      };
      idle(step);
    };

    const isActiveSrc = (src) => {
      const activeSrc = activeImage.currentSrc || activeImage.src;
      return activeSrc === src || activeImage.src === src;
    };

    const prepareStandbyImage = (src) => {
      if (standbyImage.src !== src) standbyImage.src = src;
      return waitForImage(standbyImage);
    };

    const activateStandbyImage = () => {
      const previousImage = activeImage;
      const nextImage = standbyImage;

      previousImage.classList.remove('is-active');
      previousImage.removeAttribute('data-product-spin-image');
      previousImage.setAttribute('aria-hidden', 'true');
      previousImage.setAttribute('alt', '');

      nextImage.classList.add('is-active');
      nextImage.setAttribute('data-product-spin-image', '');
      nextImage.removeAttribute('aria-hidden');
      nextImage.setAttribute('alt', imageAlt);

      activeImage = nextImage;
      standbyImage = previousImage;
    };

    const showFrame = (index) => {
      const next = clamp(index);
      const token = renderToken + 1;
      renderToken = token;
      frame = next;
      markSpinActive();
      updateStatus();
      preloadAround(next, saveData || slowConnection ? 1 : 4);

      loadFrame(next).then((src) => {
        if (renderToken !== token || frame !== next || !src || isActiveSrc(src)) return null;
        return prepareStandbyImage(src).then((ready) => {
          if (renderToken !== token || frame !== next || !ready) return;
          activateStandbyImage();
        });
      });
    };

    const applyConfig = (nextConfig) => {
      const normalized = normalizeConfig(nextConfig);
      if (!normalized) return;
      if (activeConfig && activeConfig.base === normalized.base && activeConfig.count === normalized.count) return;

      activeConfig = normalized;
      frames = buildFrames(activeConfig);
      frame = initialFrameIndex();
      decodedFrames = new Map([[frame, frames[frame]]]);
      pendingFrames = new Map();
      idleStarted = false;
      renderToken += 1;
      wheelDelta = 0;
      posterActive = Boolean(posterMode && activeConfig.poster && !controlsReady);

      activeImage.src = posterActive ? activeConfig.poster : frames[frame];
      activeImage.classList.add('is-active');
      activeImage.setAttribute('data-product-spin-image', '');
      activeImage.removeAttribute('aria-hidden');
      activeImage.setAttribute('alt', imageAlt);
      standbyImage.removeAttribute('src');
      standbyImage.classList.remove('is-active');
      standbyImage.removeAttribute('data-product-spin-image');
      standbyImage.setAttribute('aria-hidden', 'true');
      standbyImage.setAttribute('alt', '');

      viewer.dataset.frameBase = activeConfig.base;
      viewer.dataset.frameCount = String(activeConfig.count);
      viewer.dataset.frameVersion = activeConfig.version || '';
      viewer.dataset.posterUrl = activeConfig.poster || '';
      viewer.classList.remove('is-spin-active');
      syncPosterClass();
      setControlsReady(controlsReady || !posterActive);
      updateLinkedThumb(activeConfig.poster);
      scheduleSpinSlideFocus();

      updateStatus();
      preloadAround(frame, saveData || slowConnection ? 1 : 4);
    };

    const variantIdFromSelectedOptions = () => {
      const selectedOptions = selectedOptionsFromControls(viewer);
      if (!selectedOptions.values.length) return '';

      for (const [variantId, config] of variantConfigs.entries()) {
        if (!config || !Array.isArray(config.options) || !config.options.length) continue;

        const matched = config.options.every((option, index) => {
          const selectedByName = selectedOptions.byName.get(option.name);
          if (selectedByName) return normalizeOptionValue(selectedByName) === normalizeOptionValue(option.value);
          return normalizeOptionValue(selectedOptions.values[index]) === normalizeOptionValue(option.value);
        });

        if (matched) return variantId;
      }

      for (const [variantId, config] of variantConfigs.entries()) {
        if (!config || !Array.isArray(config.values) || !config.values.length) continue;
        const matched = config.values.every((value, index) => (
          normalizeOptionValue(selectedOptions.values[index]) === normalizeOptionValue(value)
        ));
        if (matched) return variantId;
      }

      if (selectedOptions.values.length === 1) {
        const selectedValue = normalizeOptionValue(selectedOptions.values[0]);
        for (const [variantId, config] of variantConfigs.entries()) {
          if (normalizeOptionValue(config && config.title) === selectedValue) return variantId;
        }
      }

      return '';
    };

    const currentVariantId = () => {
      const variantFromOptions = variantIdFromSelectedOptions();
      if (variantFromOptions && variantConfigs.has(String(variantFromOptions))) return String(variantFromOptions);

      const fields = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
      for (const field of fields) {
        const value = selectedVariantIdFromField(field);
        if (value && variantConfigs.has(String(value))) return String(value);
      }

      const variantFromUrl = new URLSearchParams(window.location.search).get('variant');
      if (variantFromUrl && variantConfigs.has(String(variantFromUrl))) return String(variantFromUrl);

      return viewer.dataset.currentVariantId || '';
    };

    const updateForVariantId = (variantId) => {
      const nextId = String(variantId || '');
      if (!nextId) return;
      if (nextId === lastVariantId) {
        syncOptionThumbs(variantConfigs.get(nextId) || activeConfig);
        return;
      }
      lastVariantId = nextId;
      viewer.dataset.currentVariantId = nextId;
      const nextConfig = variantConfigs.get(nextId) || fallbackConfig;
      applyConfig(nextConfig);
      if (mobileSpinMediaQuery.matches) activateSpinMediaMode(true);
    };

    const updateForCurrentVariant = () => {
      updateForVariantId(currentVariantId());
    };

    const stepFrame = (amount) => {
      showFrame(frame + amount);
      beginIdlePreload();
    };

    range.addEventListener('input', () => {
      showFrame(Number(range.value) - 1);
      beginIdlePreload();
    });

    range.addEventListener('change', () => {
      showFrame(Number(range.value) - 1);
      beginIdlePreload();
    });

    stage.addEventListener('wheel', (event) => {
      const rawDelta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (rawDelta === 0) return;
      const direction = rawDelta > 0 ? 1 : -1;
      const next = clamp(frame + direction);
      if (next === frame) {
        wheelDelta = 0;
        return;
      }

      event.preventDefault();
      wheelDelta += rawDelta;
      if (Math.abs(wheelDelta) < 28) return;

      stepFrame(direction);
      wheelDelta = 0;
    }, { passive: false });

    if (activateNode) {
      activateNode.addEventListener('click', activateControls);
    }

    if (posterMode) {
      stage.addEventListener('click', activateControls);
    }

    stopSpinTouchLeak(stage);
    stopSpinTouchLeak(range);
    stopSpinTouchLeak(activateNode);

    if (variantConfigs.size) {
      const scheduleVariantUpdate = () => {
        [0, 80, 240].forEach((delay) => {
          window.setTimeout(updateForCurrentVariant, delay);
        });
      };

      document.addEventListener('change', (event) => {
        if (!(event.target instanceof Element)) return;
        if (
          event.target.matches('[name="id"]') ||
          event.target.matches('.js-variants input, .js-variants select') ||
          event.target.closest('form[action*="/cart/add"]')
        ) {
          scheduleVariantUpdate();
        }
      });

      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('.js-variants label, .js-variants input, .js-variants select')) {
          scheduleVariantUpdate();
        }
      });

      ['variant:change', 'variantChange', 'product:variant-change'].forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          const variantId = event.detail && event.detail.variant && event.detail.variant.id;
          if (variantId) {
            updateForVariantId(variantId);
            return;
          }
          scheduleVariantUpdate();
        });
      });

      window.addEventListener('popstate', updateForCurrentVariant);
      window.setInterval(updateForCurrentVariant, 500);
    }

    syncPosterClass();
    bindMediaThumbs();
    syncOptionThumbs(variantConfigs.get(currentVariantId()) || activeConfig);
    setControlsReady(controlsReady);
    updateStatus();
    preloadAround(frame, saveData || slowConnection ? 1 : 4);
    updateForCurrentVariant();
    window.addEventListener('load', () => {
      idle(() => {
        preloadAround(frame, saveData || slowConnection ? 1 : 8);
        beginIdlePreload();
      });
    }, { once: true });
    });
  };

  window.__acsInitializeProductSpinViewers = initializeProductSpinViewers;

  const boot = () => initializeProductSpinViewers(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeProductSpinViewers(event.target);
  });
  window.addEventListener('pageshow', boot);
})();
